import { loadSwiper } from './swiper-loader.js';
import { loadFancybox } from './fancybox-loader.js';

const LIGHTBOX_CLICK_THRESHOLD = 10;
const LIGHTBOX_CLICK_SUPPRESS_MS = 300;

if (!customElements.get('product-media')) {
    customElements.define('product-media', class extends HTMLElement {
        #hasDragged = false;
        #pointerStart = null;
        #suppressClickUntil = 0;

        constructor() {
            super();

            this.swiper = null;
            this.Fancybox = null;
            this.fancyboxSelector = '[data-fancybox^="gallery-"]';
        }

        connectedCallback() {
            this.initCarousel();
            this.addEventListener('click', this.#handleClick, true);
            this.addEventListener('pointerdown', this.#handlePointerDown);
        }

        disconnectedCallback() {
            this.removeEventListener('click', this.#handleClick, true);
            this.removeEventListener('pointerdown', this.#handlePointerDown);
            this.#unbindPointerTracking();
            loadFancybox().then((Fancybox) => this.#destroyLightbox(Fancybox));
        }

        async initCarousel() {
            const elCarousel = this.querySelector('.product-media--carousel');
            const elScrollBar = this.querySelector('[data-scrollbar]');
            const enabled = this.querySelectorAll('.product-media--slide').length > 1;
            this.swiper && this.swiper.destroy(true, true);
            this.#hasDragged = false;

            const Swiper = await loadSwiper();
            this.swiper = new Swiper(elCarousel, {
                enabled: enabled,
                loop: false,
                slidesPerView: 'auto',
                spaceBetween: 8,
                slidesOffsetBefore: enabled ? 12 : 0,
                slidesOffsetAfter: enabled ? 12 : 0,
                ...(!enabled && { mousewheel: false }),
                ...(enabled && {
                    scrollbar: {
                        el: elScrollBar,
                        draggable: true,
                    },
                }),
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'View previous slide',
                    nextSlideMessage: 'View next slide',
                },
                on: {
                    sliderFirstMove: () => {
                        this.#markAsDragged();
                    },
                },
                breakpoints: {
                    1024: {
                        slidesOffsetBefore: 0,
                        slidesOffsetAfter: 0,
                    }
                }
            });

            this.setAttribute('data-swiper-ready', '');

            try {
                await this.initLightbox();
            } catch (error) {
                console.error('product-media lightbox init failed', error);
            }
        }

        async initLightbox() {
            if (!this.querySelector(this.fancyboxSelector)) return;

            const Fancybox = await loadFancybox();
            this.Fancybox = Fancybox;
            this.#destroyLightbox(Fancybox);
        }

        setVariantTags({ discount = '', freeShipping = false } = {}) {
            const elTags = this.querySelector('[data-media-tags]') ?? this;
            const elSaleTag = elTags.querySelector('[data-sale-tag]');
            const elShippingTag = elTags.querySelector('[data-shipping-tag]');
            const hasDiscount = Boolean(discount);

            if (elSaleTag) {
                elSaleTag.toggleAttribute('hidden', !hasDiscount);
                elSaleTag.textContent = hasDiscount ? discount : '';
            }

            if (elShippingTag) {
                elShippingTag.toggleAttribute('hidden', !freeShipping);
            }
        }

        setVariantMedia(templateEl) {
            if (!templateEl || !templateEl.content) return;

            const elWrapper = this.querySelector('.swiper-wrapper');
            if (!elWrapper) return;

            this.removeAttribute('data-swiper-ready');

            const fragment = templateEl.content.cloneNode(true);
            const elImages = fragment.querySelectorAll('img');

            for (const elImage of elImages) {
                elImage.setAttribute('loading', 'eager');
                elImage.setAttribute('fetchpriority', 'high');
            }

            elWrapper.replaceChildren(fragment);

            this.initCarousel();
        }

        #destroyLightbox(Fancybox) {
            if (!Fancybox) return;

            Fancybox.close(true);
        }

        #handlePointerDown = (event) => {
            if (event.isPrimary === false) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            this.#hasDragged = false;
            this.#pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
            this.#bindPointerTracking();
        };

        #handlePointerMove = (event) => {
            if (event.pointerId !== this.#pointerStart?.id) return;

            const deltaX = event.clientX - this.#pointerStart.x;
            const deltaY = event.clientY - this.#pointerStart.y;

            if (Math.hypot(deltaX, deltaY) >= LIGHTBOX_CLICK_THRESHOLD) {
                this.#markAsDragged();
                this.#unbindPointerTracking();
            }
        };

        #handlePointerUp = (event) => {
            if (event.pointerId !== this.#pointerStart?.id) return;
            this.#unbindPointerTracking();
        };

        #bindPointerTracking() {
            this.#unbindPointerTracking();
            window.addEventListener('pointermove', this.#handlePointerMove, { capture: true, passive: true });
            window.addEventListener('pointerup', this.#handlePointerUp, { capture: true, passive: true });
            window.addEventListener('pointercancel', this.#handlePointerUp, { capture: true, passive: true });
        }

        #unbindPointerTracking() {
            window.removeEventListener('pointermove', this.#handlePointerMove, { capture: true });
            window.removeEventListener('pointerup', this.#handlePointerUp, { capture: true });
            window.removeEventListener('pointercancel', this.#handlePointerUp, { capture: true });
        }

        #markAsDragged() {
            this.#hasDragged = true;
            this.#suppressClickUntil = Date.now() + LIGHTBOX_CLICK_SUPPRESS_MS;
        }

        #isLightboxClickSuppressed() {
            return this.#hasDragged || Date.now() < this.#suppressClickUntil;
        }

        #handleClick = (event) => {
            const elTrigger = event.target.closest('[data-lightbox-trigger]');
            if (elTrigger && this.contains(elTrigger)) {
                event.preventDefault();
                this.#openLightboxFromSlide(elTrigger.closest('.product-media--slide'));
                return;
            }

            if (this.#isLightboxClickSuppressed()) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            const elMedia = event.target.closest('.product-media--media');
            if (!elMedia || !this.contains(elMedia)) return;

            event.preventDefault();
            event.stopPropagation();
            this.#openLightboxFromSlide(elMedia.closest('.product-media--slide'));
        };

        #openLightboxFromSlide(elSlide) {
            const elSource = elSlide?.querySelector('[data-fancybox]');
            if (!elSource) return;

            if (this.Fancybox) {
                const galleryId = elSource.getAttribute('data-fancybox');
                const elSources = galleryId
                    ? [...this.querySelectorAll(`[data-fancybox="${galleryId}"]`)]
                    : [elSource];
                const startIndex = elSources.indexOf(elSource);

                this.Fancybox.fromNodes(elSources, {
                    startIndex: Math.max(0, startIndex),
                    ...this.#getLightboxOptions(this.dataset.i18nSlideOf || 'Slide __CURRENT__ of __TOTAL__'),
                });
            } else {
                elSource.click();
            }
        }

        #getLightboxOptions(slideLabelTemplate) {
            return {
                Carousel: {
                    formatCaption: () => '',
                },
                on: {
                    'Carousel.ready Carousel.change': (fancybox) => {
                        this.#updateFancyboxA11y(fancybox, slideLabelTemplate);
                    },
                    'Carousel.Panzoom.ready': () => {
                        this.#labelFancyboxToolbar();
                    },
                },
            };
        }

        #updateFancyboxA11y(fancybox, slideLabelTemplate) {
            const slide = fancybox.getSlide();
            const slides = fancybox.getCarousel().getSlides();
            const currentIndex = slide.index;
            const label = slideLabelTemplate
                .replace('__CURRENT__', String(currentIndex + 1))
                .replace('__TOTAL__', String(slides.length));

            setTimeout(() => {
                document.querySelectorAll('.fancybox__container .fancybox__slide').forEach((item, index) => {
                    item.setAttribute('role', 'group');

                    if (index === currentIndex) {
                        item.setAttribute('aria-label', label);
                    }
                });

                const elSlides = this.querySelectorAll('.product-media--slide');
                const elActiveMedia = elSlides[currentIndex]?.querySelector('.product-media--media img');

                document.querySelectorAll('.f-thumbs__viewport .f-thumbs__slide button').forEach((item) => {
                    item.removeAttribute('aria-current');
                    item.setAttribute('tabindex', '0');

                    if (item.parentElement?.classList.contains('is-selected')) {
                        item.setAttribute('aria-current', 'true');
                    }

                    const elThumbImg = item.querySelector('img');
                    if (elThumbImg && elActiveMedia?.alt) {
                        elThumbImg.alt = elActiveMedia.alt;
                    }
                });

                const elCounter = document.querySelector('.f-counter');
                if (elCounter) {
                    elCounter.setAttribute('aria-label', label);
                    elCounter.setAttribute('role', 'status');

                    for (const elSpan of elCounter.querySelectorAll('span')) {
                        elSpan.setAttribute('aria-hidden', 'true');
                    }
                }

                this.#labelFancyboxToolbar();
            }, 100);
        }

        #labelFancyboxToolbar() {
            setTimeout(() => {
                for (const elButton of document.querySelectorAll('.f-carousel__toolbar .f-button[title]')) {
                    const title = elButton.getAttribute('title');
                    if (title) {
                        elButton.setAttribute('aria-label', title);
                    }
                }
            }, 100);
        }
    });
}
